import { CANNON_RARITIES, type CannonRarity } from '@kenocyber/shared';
import type { DevCommand } from '../game/bridge';

interface DevPanelProps {
  /** DEV ONLY cadence boost. Never changes how a shot is drawn. */
  readonly rapidFire: boolean;
  readonly rarity: CannonRarity;
  readonly level: number;
  readonly onCommand: (command: DevCommand) => void;
  readonly onCannonChange: (rarity: CannonRarity, level: number) => void;
  /**
   * Why 3D was refused, if it was.
   *
   * Pets are 3D officially, so there is no renderer to choose any more - what
   * remains is telling a developer when the still fallback is showing and why,
   * because a silent fallback reads as "the pet vanished".
   */
  readonly petRenderError: string | null;
}

const LEVELS = [1, 2, 3, 4, 5, 6, 7] as const;

const RARITY_LABEL: Record<string, string> = {
  excellent: 'Excellent',
  perfect: 'Perfect',
  anomaly: 'Anomaly',
};

/**
 * DEV ONLY - rendered exclusively when the build allows dev tools AND
 * `?debug=1` is in the URL. Never present in a production bundle.
 */
export function DevPanel({
  rapidFire,
  rarity,
  level,
  onCommand,
  onCannonChange,
  petRenderError,
}: DevPanelProps) {
  return (
    <div className="dev-panel" data-testid="dev-panel">
      <div className="dev-panel-row">
        <span className="dev-tag">DEBUG</span>
        <button type="button" data-testid="dev-kill" onClick={() => onCommand('kill-ship')}>
          Matar nave
        </button>
        <button
          type="button"
          data-testid="dev-rapid-fire"
          onClick={() => onCommand('toggle-rapid-fire')}
        >
          Cadência: {rapidFire ? 'ON' : 'OFF'}
        </button>
        {/* DEV ONLY: força a próxima salva a trazer uma bomba, se a nave tiver a joia. */}
        <button type="button" data-testid="dev-force-bomb" onClick={() => onCommand('force-bomb')}>
          Bomba na próxima salva
        </button>
        {/* DEV ONLY: força a próxima salva a lançar um míssil, se a nave tiver a joia. */}
        <button
          type="button"
          data-testid="dev-force-missile"
          onClick={() => onCommand('force-missile')}
        >
          Míssil na próxima salva
        </button>
        {/* DEV ONLY: força o próximo acerto direto a envenenar, se a nave tiver a joia. */}
        <button
          type="button"
          data-testid="dev-force-poison"
          onClick={() => onCommand('force-poison')}
        >
          Veneno no próximo acerto
        </button>
        {/* DEV ONLY: força o próximo acerto direto a MARCAR, se a nave tiver a joia. */}
        <button
          type="button"
          data-testid="dev-force-bleeding"
          onClick={() => onCommand('force-bleeding')}
        >
          Sangramento no próximo acerto
        </button>
      </div>
      {petRenderError ? (
        <div className="dev-panel-row">
          <span className="dev-tag">PET</span>
          <span className="dev-note" data-testid="dev-pet-render-error" role="status">
            3D indisponível, mostrando miniatura: {petRenderError}
          </span>
        </div>
      ) : null}
      <div className="dev-panel-row">
        <label className="dev-field">
          <span>Canhão</span>
          <select
            data-testid="dev-cannon-rarity"
            value={rarity}
            onChange={(event) => onCannonChange(event.target.value as CannonRarity, level)}
          >
            {CANNON_RARITIES.map((option) => (
              <option key={option} value={option}>
                {RARITY_LABEL[option] ?? option}
              </option>
            ))}
          </select>
        </label>
        <label className="dev-field">
          <span>Nível</span>
          <select
            data-testid="dev-cannon-level"
            value={level}
            onChange={(event) => onCannonChange(rarity, Number(event.target.value))}
          >
            {LEVELS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="dev-note">Trocar o canhão reinicia a sessão de teste.</p>
    </div>
  );
}
